/** Command palette (UX review A3) — ⌘K / Ctrl+K quick navigation.
 *
 * Fuzzy-substring search across every nav destination plus a few quick
 * actions (toggle theme, logout). Keeps operators out of the sidebar for
 * the 20-page nav tree.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { NAV_GROUPS } from "../navigation/GroupedNav";

interface PaletteItem {
  label: string;
  group: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onToggleTheme: () => void;
}

/** Simple case-insensitive substring match over label + group + keywords. */
function matches(item: PaletteItem, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    item.label.toLowerCase().includes(q) ||
    item.group.toLowerCase().includes(q) ||
    (item.keywords ?? "").toLowerCase().includes(q)
  );
}

export default function CommandPalette({ open, onClose, onToggleTheme }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset the query each time the palette opens and focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      // Focus after the dialog mounts its content
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const items: PaletteItem[] = useMemo(() => {
    const navItems: PaletteItem[] = NAV_GROUPS.flatMap((g) =>
      g.items.map((i) => ({
        label: i.text,
        group: g.group,
        icon: i.icon,
        action: () => navigate(i.path),
        keywords: i.path,
      }))
    );

    const actions: PaletteItem[] = [
      {
        label: "Toggle dark mode",
        group: "Actions",
        icon: <SearchIcon />,
        action: () => onToggleTheme(),
        keywords: "theme dark light appearance",
      },
    ];

    return [...navItems, ...actions];
  }, [navigate, onToggleTheme]);

  const filtered = useMemo(
    () => (query ? items.filter((i) => matches(i, query)).slice(0, 12) : items.slice(0, 12)),
    [items, query]
  );

  const runItem = (item: PaletteItem) => {
    onClose();
    item.action();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: "10vh" } }}
      aria-label="Command palette"
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search pages and actions…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filtered.length > 0) {
              e.preventDefault();
              runItem(filtered[0]);
            } else if (e.key === "Escape") {
              onClose();
            }
          }}
          variant="filled"
          sx={{
            "& .MuiFilledInput-root": { borderRadius: 0, py: 1.5 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <List dense sx={{ pt: 0, maxHeight: 400, overflowY: "auto" }}>
          {filtered.map((item) => (
            <ListItemButton key={`${item.group}-${item.label}`} onClick={() => runItem(item)}>
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {item.group}
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No matches for “{query}”
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}
